import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import clsx from 'clsx';
import parse, { domToReact, Element } from 'html-react-parser';
import { createElement, ElementType, useRef } from 'react';

import LexicalEditorContent from '~components/generic/editable-field/components/LexicalEditorContent';
import { CustomParagraphNode } from '~components/generic/editable-field/components/nodes/CustomParagraphNode';
import { Link } from '~components/generic/elements/Link/Link';
import { getEntityFieldDefinition } from '~services/entity-definitions';
import ExampleTheme from '../Theme';
import cssStyles from '~components/generic/editable-field/EditableField/EditableField.css';
type ClassNameShape = string | { [key: string]: boolean };

const editorConfig = {
  namespace: 'Lexical Editor',
  // Handling of errors during update
  onError(error: Error) {
    throw error;
  },
  nodes: [
    CustomParagraphNode,
    HeadingNode,
    ListNode,
    ListItemNode,
    QuoteNode,
    CodeNode,
    CodeHighlightNode,
    TableNode,
    TableCellNode,
    TableRowNode,
    AutoLinkNode,
    LinkNode,
  ],
  // The editor theme
  theme: ExampleTheme,
};

type SlateEditorProps = {
  editing: boolean;
  fieldName: string;
  onChange?: (field: string, value: string) => void;
  data?: string;
  entityType?: string;
  hide?: boolean;
  className?: ClassNameShape;
  replaceFn?: (value: string) => string;
  index?: number;
};

const EditableFieldRawHtml = (props: SlateEditorProps) => {
  const { entityType, onChange, data, editing, fieldName, className, index, hide } = props;

  const targetRef = useRef(null);

  const classes = clsx(
    {
      'c-module__field--editing': editing,
      [cssStyles.base]: true,
      [cssStyles.hide]: hide,
    },
    className,
  );

  const fieldDef = getEntityFieldDefinition(entityType, fieldName);

  // Updated: Use ElementType instead of keyof JSX.IntrinsicElements for proper React typing
  const tagName = (fieldDef && fieldDef.tagName) || 'div';
  const DynamicTag = tagName as ElementType;

  const onChangeEditor = html => {
    if (fieldDef?.multiple) {
      return onChange(`${fieldName}.${index}`, html);
    }

    onChange(fieldName, html);
  };

  if (editing) {
    return (
      <LexicalComposer initialConfig={editorConfig}>
        <LexicalEditorContent showAsHtmlCode={true} targetRef={targetRef} {...props} onChangeEditor={onChangeEditor} />
      </LexicalComposer>
    );
  }

  return createElement(
    DynamicTag,
    { ref: targetRef, className: classes },
    parse(data || '', {
      replace: domNode => {
        if (domNode instanceof Element) {
          const { type, name, attribs, children } = domNode;
          if (type !== 'tag' || name !== 'a') {
            return;
          }
          const { href } = attribs;
          if (href) {
            const [path, id] = href.indexOf('/#/') === -1 && href.indexOf('~') >= 1 ? href.split('#') : [href, ''];
            return createElement(Link, { id, url: !id && path }, domToReact(children as any));
          }
        }
        return;
      },
    }),
  );
};

export default EditableFieldRawHtml;
